import { DOCUMENT } from '@angular/common';
import { Component, HostListener, inject } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, Router, RouterOutlet } from '@angular/router';
import { filter, take } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.html',
  styleUrl: './app.css',
})
export class App {
  private readonly doc = inject(DOCUMENT);
  private readonly router = inject(Router);

  /**
   * The address's #section changed (a link to another page's section, the back button):
   * the router scrolls to it as it navigates, but a navigation still in flight (the page's
   * first) can scroll back to the top after it. Scroll to the section once the router is done.
   */
  @HostListener('window:hashchange')
  onHashChange(): void {
    const id = decodeURIComponent(this.doc.defaultView?.location.hash.slice(1) ?? '');
    if (!id) return;
    const win = this.doc.defaultView;
    const scrollTo = (onlyIfReset: boolean) => {
      const target = this.doc.getElementById(id);
      if (!target || !win || Math.abs(target.getBoundingClientRect().top) < 120) return;
      // the later checks only undo a reset to the top, never a reader's own scrolling
      if (onlyIfReset && win.scrollY > 10) return;
      target.scrollIntoView({ block: 'start' });
    };
    const go = () => {
      setTimeout(() => scrollTo(false), 120);
      setTimeout(() => scrollTo(true), 600);
      setTimeout(() => scrollTo(true), 1500);
    };
    if (this.router.currentNavigation()) {
      this.router.events
        .pipe(
          filter((e) => e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError),
          take(1),
        )
        .subscribe(go);
    } else {
      go();
    }
  }

  /**
   * In-page links (`href="#section"`): with `<base href="/">` the browser would resolve them
   * against the site root and leave the page for the home. Scroll to the section instead,
   * keep the address in step, and move focus there for keyboard and screen-reader users.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = (event.target as Element | null)?.closest?.('a[href^="#"]') as HTMLAnchorElement | null;
    if (!link || link.target === '_blank') return;
    event.preventDefault();
    const id = decodeURIComponent(link.getAttribute('href')!.slice(1));
    const win = this.doc.defaultView;
    const smooth = !win?.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const target = id ? this.doc.getElementById(id) : null;
    if (!target) {
      win?.scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'auto' });
      return;
    }
    target.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
    win?.history.replaceState(win.history.state, '', `${win.location.pathname}${win.location.search}#${encodeURIComponent(id)}`);
    if (!target.matches('a, button, input, select, textarea, [tabindex]')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }
}
